import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    DocumentTemplateSummary,
    ElectronicDocumentTemplateForm,
} from '../../../types/documentTemplate/ElectronicDocumentTemplateTypes';

interface ElectronicDocumentTemplateState {
    form: ElectronicDocumentTemplateForm;
    list: DocumentTemplateSummary[];
    paginator: any;
    isSubmitting: boolean;
    error: string | null;
    showRegistrationModal: boolean;
    showEditModal: boolean;
    showPreviewModal: boolean;
    htmlPreview: string,
}

const initialState: ElectronicDocumentTemplateState = {
    form: {
        id: null,
        documentTypeId: 0,
        description: '',
        version: ''
    },
    isSubmitting: false,
    error: null,
    list: [],
    paginator: null,
    showRegistrationModal: false,
    showEditModal: false,
    showPreviewModal: false,
    htmlPreview: ''
};

const electronicDocumentTemplateSlice = createSlice({
    name: 'electronicDocumentTemplate',
    initialState,
    reducers: {
        setSubmitting(state, action: PayloadAction<boolean>) {
            state.isSubmitting = action.payload;
        },
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        setList(state, action: PayloadAction<DocumentTemplateSummary[]>) {
            // console.log(action.payload);
            state.list = action.payload;
        },
        setTemplate(state, action: PayloadAction<ElectronicDocumentTemplateForm>) {
            state.form = action.payload;
            state.error = null;
        },
        updateTemplateField(
            state,
            action: PayloadAction<{ field: keyof ElectronicDocumentTemplateForm; value: any }>
        ) {
            const { field, value } = action.payload;
            state.form = {
                ...state.form,
                [field]: value,
            };
        },
        resetTemplate(state) {
            state.form = initialState.form;
            state.isSubmitting = false;
            state.error = null;
        },
        setShowRegistrationModal(state, action: PayloadAction<boolean>) {
            state.showRegistrationModal = action.payload;
        },
        setShowEditModal(state, action: PayloadAction<boolean>) {
            state.showEditModal = action.payload;
        },
        setShowPreviewModal(state, action: PayloadAction<boolean>) {
            state.showPreviewModal = action.payload;
        },
        closeRegistrationModalAndReset(state) {
            state.showRegistrationModal = false;
            state.form = initialState.form;
            state.isSubmitting = false;
            state.error = null;
        },
        clearForm(state) {
            state.showRegistrationModal = false;
            state.form = initialState.form;
            state.isSubmitting = false;
            state.error = null;
        },
        setHtmlPreview(state, action: PayloadAction<string>) {
            state.htmlPreview = action.payload;
        },
        clearHtmlPreview(state) {
            state.htmlPreview = '';
        },
        closeEditModalAndReset(state) {
            state.showEditModal = false;
            state.form = initialState.form;
            state.isSubmitting = false;
            state.error = null;
        },
        updateTemplateStatus(state, action: PayloadAction<{ id: number; active: boolean }>) {
            const { id, active } = action.payload;
            const index = state.list.findIndex(template => template.id === id);
            if (index !== -1) {
                state.list[index].active = active;
            }
        }
    },
});

export const {
    setSubmitting,
    setList,
    setError,
    setTemplate,
    updateTemplateField,
    resetTemplate,
    setShowRegistrationModal,
    setShowEditModal,
    setShowPreviewModal,
    closeRegistrationModalAndReset,
    closeEditModalAndReset,
    clearForm,
    setHtmlPreview,
    clearHtmlPreview,
    updateTemplateStatus
} = electronicDocumentTemplateSlice.actions;

export default electronicDocumentTemplateSlice.reducer;
